import React, { Component } from 'react';
import { connect } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  Button,
  SelectFormItem,
  InputNumberFormItem,
  InputTimePickerFormItem,
  ItemActions,
  basicStyle,
  Row,
  Col,
  CurrencyInput,
  DatePickerFormItem,
} from '@jjj/components';
import moment from 'moment';
import { isArray } from 'lodash';
import { getDaysOfWeekList } from '../../../../../../helpers/utility';
import { getCurrencyConfig } from '../../../../../../helpers/countriesHelpers';
import { defaultPenaltyValue } from '../../reducer';
import actions from '../../actions';
import { Wrapper, AddRateLabel } from './penaltyRatesSection.style';
import { t } from '../../../../../../i18n';
import { is12HoursTimeFormat, getTimeFormat, getDateFormat } from '../../../../../../helpers/dateTime';
import { getRatesAndRulesFeatureFlag } from '../../constants';

class PenaltyRates extends Component {
  onAddPenalty = () => {
    const index = this.props.data.editForm[this.getPenaltyRatesKey()].length;
    this.props.changeNestedProperty({ attrs: [this.getPenaltyRatesKey(), index], value: { ...defaultPenaltyValue } });
  };

  onPenaltyChange = (attrs, value) => {
    this.props.changeNestedProperty({ attrs: [this.getPenaltyRatesKey(), ...attrs], value });
  };

  removePenalty = index => {
    this.props.changeNestedProperty({ attrs: [this.getPenaltyRatesKey(), index, '_destroy'], value: true });
  };

  getPenaltyRatesKey = () => `${this.props.type}_penalty_rates_group`;

  isDateDisabled = (type, currentDate) => {
    if (type === 'current') {
      return currentDate && currentDate >= moment().endOf('day');
    }

    return currentDate && currentDate < moment().endOf('day');
  };

  render() {
    const { gutter } = basicStyle;
    const { data, type } = this.props;
    const { editForm } = data;
    const { locale, currency } = getCurrencyConfig();

    const startDate = editForm[`${type}_start_date`];

    const penaltyRates = isArray(editForm[this.getPenaltyRatesKey()]) ? editForm[this.getPenaltyRatesKey()] : [];

    return (
      <Wrapper>
        <Row gutter={gutter}>
          <Col xl={5} lg={5} md={5} sm={5} xs={5}>
            <DatePickerFormItem
              isRequired
              labelName={t('dash.admin.sectors.start_date')}
              dateFormat={getDateFormat('date_numeric_long')}
              value={startDate?.value || moment().toISOString()}
              validateStatus={startDate?.error && startDate.touched ? 'error' : ''}
              help={startDate?.touched ? startDate?.error : ''}
              onChange={(_event, value) => this.props.changeNestedProperty({ attrs: [`${type}_start_date`], value })}
              attribute={`${type}_start_date`}
              disabledDate={current => this.isDateDisabled(type, current)}
            />
          </Col>
        </Row>

        {penaltyRates.map((penalty, index) => {
          const penaltyKey = `${penalty.id?.value} - ${index}`;

          // eslint-disable-next-line no-underscore-dangle
          return penalty._destroy ? null : (
            <Row gutter={gutter} key={penaltyKey}>
              <Col xl={5} lg={5} md={5} sm={5} xs={5}>
                <SelectFormItem
                  labelName={t('dash.admin.sectors.day_of_the_week_label')}
                  value={penalty.day && penalty.day.value ? penalty.day.value : null}
                  validateStatus={editForm.day && editForm.day.error && editForm.day.touched ? 'error' : ''}
                  help={editForm.day && editForm.day.error && editForm.day.touched ? editForm.day.error : ''}
                  onChange={day => this.onPenaltyChange([index, 'day'], day)}
                  optionValues={getDaysOfWeekList()}
                  data-cy="admin-sectors-penalty_rate-day_of_the_week-select"
                />
              </Col>
              <Col xl={4} lg={4} md={4} sm={4} xs={4} style={{ width: '130px' }}>
                <InputTimePickerFormItem
                  labelName={t('dash.admin.sectors.columns.start_time_title')}
                  use12Hours={is12HoursTimeFormat()}
                  format={getTimeFormat('time_hours_minutes_long')}
                  value={penalty.start_time && penalty.start_time.value ? penalty.start_time.value : 0}
                  validateStatus={
                    editForm.start_time && editForm.start_time.error && editForm.start_time.touched ? 'error' : ''
                  }
                  help={
                    editForm.start_time && editForm.start_time.error && editForm.start_time.touched
                      ? editForm.start_time.error
                      : ''
                  }
                  onChange={value => this.onPenaltyChange([index, 'start_time'], value)}
                  min={0}
                />
              </Col>
              <Col xl={4} lg={4} md={4} sm={4} xs={4} style={{ width: '130px' }}>
                <InputTimePickerFormItem
                  labelName={t('dash.admin.sectors.columns.end_time_title')}
                  use12Hours={is12HoursTimeFormat()}
                  format={getTimeFormat('time_hours_minutes_long')}
                  value={penalty.end_time && penalty.end_time.value ? penalty.end_time.value : 0}
                  validateStatus={
                    editForm.end_time && editForm.end_time.error && editForm.end_time.touched ? 'error' : ''
                  }
                  help={
                    editForm.end_time && editForm.end_time.error && editForm.end_time.touched
                      ? editForm.end_time.error
                      : ''
                  }
                  onChange={value => this.onPenaltyChange([index, 'end_time'], value)}
                />
              </Col>
              <Col xl={5} lg={5} md={5} sm={5} xs={5}>
                <InputNumberFormItem
                  labelName={t('dash.admin.sectors.columns.penalties/markups_title')}
                  min={getRatesAndRulesFeatureFlag() ? 125 : 100}
                  formatter={value => `${value}%`}
                  value={penalty.multiplier && penalty.multiplier.value ? penalty.multiplier.value : null}
                  validateStatus={
                    editForm.multiplier && editForm.multiplier.error && editForm.multiplier.touched ? 'error' : ''
                  }
                  help={
                    editForm.multiplier && editForm.multiplier.error && editForm.multiplier.touched
                      ? editForm.multiplier.error
                      : ''
                  }
                  onChange={value => this.onPenaltyChange([index, 'multiplier'], value)}
                  data-cy="admin_sector-penalty_rates-multiplier-input"
                />
              </Col>
              <Col xl={5} lg={5} md={5} sm={5} xs={5}>
                <CurrencyInput
                  locale={locale}
                  defaultValue={0}
                  currency={currency}
                  labelName={t('dash.admin.sectors.flat_fee_per_hour_label')}
                  value={penalty.hourly_fee && penalty.hourly_fee.value ? penalty.hourly_fee.value : 0}
                  validateStatus={
                    editForm.hourly_fee && editForm.hourly_fee.error && editForm.hourly_fee.touched ? 'error' : ''
                  }
                  help={
                    editForm.hourly_fee && editForm.hourly_fee.error && editForm.hourly_fee.touched
                      ? editForm.hourly_fee.error
                      : ''
                  }
                  onChange={value => this.onPenaltyChange([index, 'hourly_fee'], value)}
                  data-cy="admin-sector-penalty_rate_section-penalty_rates-flat_fee_per_hour-currency_input"
                />
              </Col>
              <Col xl={1} lg={1} md={1} sm={1} xs={1} className="rowActions">
                <ItemActions
                  closeDropdownOnClick
                  placement="bottomRight"
                  actionsList={[
                    {
                      name: t('dash.common.button.remove_label'),
                      onActionClick: () => this.removePenalty(index),
                    },
                  ]}
                />
              </Col>
              {penalty.day && penalty.day.error && (
                <Col xs={24} className="baseError">
                  {penalty.day.error.join('. ')}
                </Col>
              )}
            </Row>
          );
        })}
        <Button
          type="link"
          className="addNewPosition"
          onClick={this.onAddPenalty}
          data-cy="admin-sector-penalty-rates-add-button"
        >
          <FontAwesomeIcon icon="plus-circle" />
          <AddRateLabel>
            {editForm[this.getPenaltyRatesKey()]?.length
              ? t('dash.admin.sectors.add_another_rate_label')
              : t('dash.admin.sectors.add_rate_label')}
          </AddRateLabel>
        </Button>
      </Wrapper>
    );
  }
}

export default connect(
  state => ({
    data: state.Sector,
  }),
  dispatch => ({
    changeNestedProperty: /* istanbul ignore next */ data => dispatch(actions.changeNestedProperty(data)),
  }),
)(PenaltyRates);
